import { Box, Button, Grid, TextField, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import { AppDispatch } from "../../redux/store";
import { useAddBreefSettingForm } from "../../utils/forms";
import { useTranslation } from "react-i18next";
import { getBreefSetting, updateBreefSetting } from "../../service/BreefSettingService";
import SmsSetting from "./SmsSetting";
import VideoConversions from "./VideoConversions";

const UpdateBreefSetting = () => {
  const [t] = useTranslation("global");
  // const isEdit = new URLSearchParams(location.search).get("isEdit");
  const [breefSettingId, setBreefSettingId] = useState<string>("");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isEditing, setIsEditing] = useState(false);

  const BreefSettingformik = useAddBreefSettingForm(isEditing, breefSettingId || "", isMobile);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const fetchBreefAndSmsSetting = async (page: number = 0) => {
    const fetchedSettings = await getBreefSetting(dispatch, isMobile, navigate, page + 1);
    if (fetchedSettings) {
      console.log("Fetched tenants: ", fetchedSettings);

      setBreefSettingId(fetchedSettings.results[0]?._id);
      const saved = fetchedSettings.results[0];
      BreefSettingformik.setValues({
        quizMaxPoints: saved?.quizMaxPoints ?? 2000,
        gameMaxPoints: saved?.gameMaxPoints ?? 1000,
        speedSharePercent: saved?.speedSharePercent ?? 25,
        speedSeconds: saved?.speedSeconds ?? 45,
        retryPercent: saved?.retryPercent ?? 60,
        maxVideoMinutes: (saved?.maxVideoSeconds || 240) / 60,
        maxVideoMB: saved?.maxVideoMB || 1024,
      });
    }
  };
  useEffect(() => {
    fetchBreefAndSmsSetting();
  }, [dispatch, isMobile, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const data = {
        quizMaxPoints: Number(BreefSettingformik.values?.quizMaxPoints),
        gameMaxPoints: Number(BreefSettingformik.values?.gameMaxPoints),
        speedSharePercent: Number(BreefSettingformik.values?.speedSharePercent),
        speedSeconds: Number(BreefSettingformik.values?.speedSeconds),
        retryPercent: Number(BreefSettingformik.values?.retryPercent),
        maxVideoMinutes: BreefSettingformik.values?.maxVideoMinutes,
        maxVideoMB: BreefSettingformik.values?.maxVideoMB,
      };
      await updateBreefSetting(data, breefSettingId);
      setIsEditing(false);
      navigate("/breef-setting");
    } catch (err) {
      console.error("Error editing user details: ", err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };
  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <Layout>
      <PageHeader title={t("updateBreefSetting.title")} />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          marginRight: "10%",
          marginTop: "2%",
          marginBottom: "1%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          style={{ display: "flex", alignItems: "center",  }}
        >
          {/* {isEdit === "true" ? "Update" : "View"} */}
          {t("updateBreefSetting.subTitle")}
        </Typography>
      </div>
      <Box sx={{ display: "flex", flexDirection: "column",  marginBottom: "5%" }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* The breef score: the quiz and the game each give a share of a fixed maximum, so a breef is worth
              the same with three questions as with ten, and whichever of the games the customer picked */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                required
                margin="dense"
                id="quizMaxPoints"
                name="quizMaxPoints"
                label={t("updateBreefSetting.quizMaxPoints")}
                helperText={
                  (BreefSettingformik.touched.quizMaxPoints && BreefSettingformik.errors.quizMaxPoints) ||
                  t("updateBreefSetting.quizMaxPointsHelp")
                }
                type="number"
                inputProps={{ min: 0, max: 100000 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("quizMaxPoints")}
                value={BreefSettingformik.values.quizMaxPoints ?? ""}
                error={
                  BreefSettingformik.touched.quizMaxPoints && Boolean(BreefSettingformik.errors.quizMaxPoints)
                }
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="gameMaxPoints"
                name="gameMaxPoints"
                label={t("updateBreefSetting.gameMaxPoints")}
                helperText={
                  (BreefSettingformik.touched.gameMaxPoints && BreefSettingformik.errors.gameMaxPoints) ||
                  t("updateBreefSetting.gameMaxPointsHelp")
                }
                type="number"
                inputProps={{ min: 0, max: 100000 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("gameMaxPoints")}
                value={BreefSettingformik.values.gameMaxPoints ?? ""}
                error={
                  BreefSettingformik.touched.gameMaxPoints && Boolean(BreefSettingformik.errors.gameMaxPoints)
                }
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="speedSharePercent"
                name="speedSharePercent"
                label={t("updateBreefSetting.speedSharePercent")}
                helperText={
                  (BreefSettingformik.touched.speedSharePercent && BreefSettingformik.errors.speedSharePercent) ||
                  t("updateBreefSetting.speedSharePercentHelp")
                }
                type="number"
                inputProps={{ min: 0, max: 100 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("speedSharePercent")}
                value={BreefSettingformik.values.speedSharePercent ?? ""}
                error={
                  BreefSettingformik.touched.speedSharePercent && Boolean(BreefSettingformik.errors.speedSharePercent)
                }
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="speedSeconds"
                name="speedSeconds"
                label={t("updateBreefSetting.speedSeconds")}
                helperText={
                  (BreefSettingformik.touched.speedSeconds && BreefSettingformik.errors.speedSeconds) ||
                  t("updateBreefSetting.speedSecondsHelp")
                }
                type="number"
                inputProps={{ min: 1, max: 600 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("speedSeconds")}
                value={BreefSettingformik.values.speedSeconds ?? ""}
                error={
                  BreefSettingformik.touched.speedSeconds && Boolean(BreefSettingformik.errors.speedSeconds)
                }
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="retryPercent"
                name="retryPercent"
                label={t("updateBreefSetting.retryPercent")}
                helperText={
                  (BreefSettingformik.touched.retryPercent && BreefSettingformik.errors.retryPercent) ||
                  t("updateBreefSetting.retryPercentHelp")
                }
                type="number"
                inputProps={{ min: 0, max: 100 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("retryPercent")}
                value={BreefSettingformik.values.retryPercent ?? ""}
                error={
                  BreefSettingformik.touched.retryPercent && Boolean(BreefSettingformik.errors.retryPercent)
                }
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                id="maxVideoMinutes"
                name="maxVideoMinutes"
                label={t("updateBreefSetting.maxVideoMinutes")}
                helperText={t("updateBreefSetting.maxVideoMinutesHelp")}
                type="number"
                inputProps={{ min: 0.5, max: 60, step: 0.5 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("maxVideoMinutes")}
                value={BreefSettingformik.values.maxVideoMinutes ?? ""}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                id="maxVideoMB"
                name="maxVideoMB"
                label={t("updateBreefSetting.maxVideoMB")}
                type="number"
                inputProps={{ min: 10, max: 10240 }}
                fullWidth
                variant="outlined"
                onChange={BreefSettingformik.handleChange("maxVideoMB")}
                value={BreefSettingformik.values.maxVideoMB ?? ""}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            {isEditing ? (
              <>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    mr: 1,
                    "&:hover": { borderColor: "#d55a07" },
                  }}
                >
                  {t("updateTenant.t6")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ "&:hover": { backgroundColor: "#d55a07" } }}
                >
                  {t("updateTenant.t7")}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleEdit}
                //  type="submit"
                variant="contained"
                sx={{ "&:hover": { backgroundColor: "#d55a07" } }}
              >
                {t("updateBreefSetting.edit")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <VideoConversions />

      <SmsSetting />
    </Layout>
  );
};

export default UpdateBreefSetting;
